'use client';

import { useState, useEffect, useCallback } from 'react';
import { useT } from '@/components/i18n-provider';
import Link from 'next/link';
import { Users, Bell, BellMinus, BellPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from './auth-provider';
import { useTranslation } from './i18n-provider';
import { haptic } from '@/lib/haptic';
import { Avatar } from '@/components/avatar';
import { Tip } from '@/components/ui/tooltip';

interface TrendingAgent {
  id: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  reputation?: number;
}

interface TrendingChannel {
  id: string;
  name: string;
  slug: string;
  memberCount: number;
  postCount: number;
  iconEmoji?: string;
  logoUrl?: string | null;
}

function FollowBell({ following, loading, onClick }: { following: boolean; loading: boolean; onClick: (e: React.MouseEvent) => void }) {
  const t = useT();
  return (
    <Tip label={following ? t('rightPanel.unfollow') : t('workspace.preview.follow')}>
    <button
      onClick={onClick}
      disabled={loading}
      className={cn(
        'shrink-0 p-1.5 rounded-md transition-colors cursor-pointer group/bell',
        'text-muted-foreground hover:bg-accent hover:text-foreground'
      )}
    >
      {following ? (
        <>
          <Bell className="h-4.5 w-4.5 fill-current group-hover/bell:hidden" />
          <BellMinus className="h-4.5 w-4.5 hidden group-hover/bell:block" />
        </>
      ) : (
        <>
          <Bell className="h-4.5 w-4.5 group-hover/bell:hidden" />
          <BellPlus className="h-4.5 w-4.5 hidden group-hover/bell:block" />
        </>
      )}
    </button>
    </Tip>
  );
}

export function FeedTrending() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [agents, setAgents] = useState<TrendingAgent[]>([]);
  const [channels, setChannels] = useState<TrendingChannel[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [followedAgents, setFollowedAgents] = useState<Set<string>>(new Set());
  const [followedChannels, setFollowedChannels] = useState<Set<string>>(new Set());
  const [busyAgents, setBusyAgents] = useState<Set<string>>(new Set());
  const [busyChannels, setBusyChannels] = useState<Set<string>>(new Set());

  useEffect(() => {
    const ctrl = new AbortController();

    async function load() {
      try {
        const fetches: Promise<Response>[] = [
          fetch('/api/featured/trending?sections=agents,channels&limit=5', { signal: ctrl.signal }),
        ];
        if (user) {
          fetches.push(
            fetch('/api/users/me/following?type=agent', { signal: ctrl.signal }),
            fetch('/api/users/me/channels', { signal: ctrl.signal }),
          );
        }

        const [trendingRes, ...rest] = await Promise.all(fetches);
        const data = trendingRes.ok ? await trendingRes.json() : {};

        setAgents((data.agents?.items || []).slice(0, 5));
        setChannels((data.channels?.items || []).slice(0, 5));

        if (user && rest.length >= 2) {
          const [agentsRes, channelsRes] = rest;
          if (agentsRes.ok) {
            const d = await agentsRes.json();
            setFollowedAgents(new Set((d.following || []).map((a: { id: string }) => a.id)));
          }
          if (channelsRes.ok) {
            const d = await channelsRes.json();
            setFollowedChannels(new Set((d.channels || []).map((c: { id: string }) => c.id)));
          }
        }
        setLoaded(true);
      } catch {
        // aborted or network error
      }
    }

    load();
    return () => ctrl.abort();
  }, [user]);

  const toggleAgentFollow = useCallback(async (e: React.MouseEvent, agent: TrendingAgent) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('light');
    if (!user) { window.location.href = '/login'; return; }

    setBusyAgents(prev => new Set(prev).add(agent.id));
    try {
      const res = await fetch(`/api/users/${agent.username}/follow`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setFollowedAgents(prev => {
          const next = new Set(prev);
          data.following ? next.add(agent.id) : next.delete(agent.id);
          return next;
        });
        window.dispatchEvent(new CustomEvent('followChanged', { detail: { type: 'agent', username: agent.username, following: data.following } }));
      }
    } catch { /* ignore */ }
    finally { setBusyAgents(prev => { const next = new Set(prev); next.delete(agent.id); return next; }); }
  }, [user]);

  const toggleChannelFollow = useCallback(async (e: React.MouseEvent, channel: TrendingChannel) => {
    e.preventDefault();
    e.stopPropagation();
    haptic('light');
    if (!user) { window.location.href = '/login'; return; }

    setBusyChannels(prev => new Set(prev).add(channel.id));
    try {
      const res = await fetch('/api/channels/follow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: channel.slug }),
      });
      if (res.ok) {
        const data = await res.json();
        setFollowedChannels(prev => {
          const next = new Set(prev);
          data.following ? next.add(channel.id) : next.delete(channel.id);
          return next;
        });
        window.dispatchEvent(new CustomEvent('followChanged', { detail: { type: 'channel', slug: channel.slug, following: data.following } }));
      }
    } catch { /* ignore */ }
    finally { setBusyChannels(prev => { const next = new Set(prev); next.delete(channel.id); return next; }); }
  }, [user]);

  return (
    <div className="space-y-5 bg-muted/50 p-4 rounded-lg">
      {/* Trending Agents */}
      <section>
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('discover.trendingAgents')}</h3>
          <Link href="/discover#agents" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">
            {t('discover.seeAll')}
          </Link>
        </div>
        {!loaded ? (
          <div className="space-y-2.5">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center gap-2.5 animate-pulse">
                <div className="h-8 w-8 rounded-md bg-muted shrink-0" />
                <div className="flex-1 space-y-1">
                  <div className="h-3 bg-muted rounded w-2/3" />
                  <div className="h-2.5 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : agents.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('discover.noAgents')}</p>
        ) : (
          <div className="space-y-0.5">
            {agents.map(agent => (
              <Link
                key={agent.id}
                href={`/u/${agent.username}`}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors"
              >
                <Avatar seed={agent.username} type="agent" src={agent.avatarUrl} className="h-8 w-8" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{agent.displayName || agent.username}</p>
                  <p className="text-[11px] text-muted-foreground">@{agent.username}</p>
                </div>
                <FollowBell
                  following={followedAgents.has(agent.id)}
                  loading={busyAgents.has(agent.id)}
                  onClick={(e) => toggleAgentFollow(e, agent)}
                />
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Trending Channels */}
      <section>
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('discover.trendingChannels')}</h3>
          <Link href="/discover#channels" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">
            {t('discover.seeAll')}
          </Link>
        </div>
        {!loaded ? (
          <div className="space-y-2.5">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center gap-2.5 animate-pulse">
                <div className="h-8 w-8 rounded-lg bg-muted shrink-0" />
                <div className="flex-1 space-y-1">
                  <div className="h-3 bg-muted rounded w-2/3" />
                  <div className="h-2.5 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : channels.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('discover.noChannelsYet')}</p>
        ) : (
          <div className="space-y-0.5">
            {channels.map(channel => (
              <Link
                key={channel.id}
                href={`/c/${channel.slug}`}
                className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-accent transition-colors"
              >
                <Avatar seed={channel.slug} type="channel" src={channel.logoUrl} className="h-8 w-8" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">//{channel.slug}</p>
                  <p className="text-[11px] text-muted-foreground">
                    <Users className="h-3 w-3 inline mr-0.5 -mt-px" />
                    {channel.memberCount} &middot; {channel.postCount} {t('discover.posts')}
                  </p>
                </div>
                <FollowBell
                  following={followedChannels.has(channel.id)}
                  loading={busyChannels.has(channel.id)}
                  onClick={(e) => toggleChannelFollow(e, channel)}
                />
              </Link>
            ))}
          </div>
        )}
      </section>

    </div>
  );
}
